import { useState, useEffect } from 'react';
import { Loader2, UserPlus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';

interface StaffUser { user_id: string; email: string | null; role: string; }

const AdminUsers = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('admin');
  const [busy, setBusy] = useState(false);

  const call = async (body: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke('admin-users', { body });
    if (error) throw error;
    if (data?.error) throw new Error(typeof data.error === 'string' ? data.error : 'Request failed');
    return data;
  };

  const load = async () => {
    try {
      const data = await call({ action: 'list' });
      setUsers(data.users || []);
    } catch (e) {
      toast({ title: 'Could not load users', description: (e as Error).message, variant: 'destructive' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!email.trim()) return toast({ title: 'Email is required', variant: 'destructive' });
    setBusy(true);
    try {
      await call({ action: 'add', email: email.trim(), role });
      toast({ title: 'Access granted' });
      setEmail('');
      load();
    } catch (e) {
      toast({ title: 'Could not grant access', description: (e as Error).message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const remove = async (u: StaffUser) => {
    setBusy(true);
    try {
      await call({ action: 'remove', user_id: u.user_id, role: u.role });
      toast({ title: 'Access removed' });
      load();
    } catch (e) {
      toast({ title: 'Could not remove access', description: (e as Error).message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Team Access</h1>
          <p className="text-muted-foreground mt-1">Who can sign in to the CMS</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Grant access</CardTitle>
            <CardDescription>The person must already have signed up before you can grant them a role</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Input className="flex-1 min-w-[220px]" placeholder="person@example.com" value={email} onChange={e => setEmail(e.target.value)} />
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={add} disabled={busy}><UserPlus className="mr-2 h-4 w-4" />Grant</Button>
          </CardContent>
        </Card>

        <Card><CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Email</TableHead><TableHead className="w-28">Role</TableHead><TableHead className="w-16" /></TableRow></TableHeader>
              <TableBody>
                {users.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No team members yet</TableCell></TableRow>}
                {users.map(u => (
                  <TableRow key={`${u.user_id}-${u.role}`}>
                    <TableCell className="font-medium">{u.email ?? u.user_id}</TableCell>
                    <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="icon" disabled={busy} onClick={() => remove(u)}><Trash2 className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent></Card>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
